import { useEffect, useRef, useCallback, useState } from 'react'

/**
 * Hook pour gérer la vibration via Gamepad API (8BitDo + Joy-Con)
 * SIMPLIFIÉ: Accès direct aux gamepads à chaque appel, comme dans le script de test
 */
export const useGamepadVibration = () => {
  const [gamepadState, setGamepadState] = useState({
    enabled: true, // Activé par défaut pour éviter confusion
    connected: false,
    intensity: 1.0, // Intensité maximum par défaut
    status: '❌ Aucune manette détectée',
    gamepads: []
  })

  // Configuration: quel slot pour quel côté
  const sideConfigRef = useRef({ 0: 'left', 1: 'right' })

  // Scanner les manettes connectées
  const scanGamepads = useCallback(() => {
    const gamepads = navigator.getGamepads()
    const connected = []

    for (let i = 0; i < gamepads.length; i++) {
      const gp = gamepads[i]
      if (gp && gp.connected) {
        const hasVibration = !!gp.vibrationActuator
        
        connected.push({
          index: gp.index,
          id: gp.id,
          hasVibration: hasVibration,
          side: sideConfigRef.current[gp.index] || (gp.index % 2 === 0 ? 'left' : 'right')
        })
        
        console.log(`🎮 Slot ${i}:`, gp.id.substring(0, 30), hasVibration ? '✅ Vibration' : '❌ Pas de vibration')
      }
    }

    if (connected.length > 0) {
      const withVibration = connected.filter(g => g.hasVibration).length
      setGamepadState(prev => ({
        ...prev,
        connected: true,
        status: `✅ ${connected.length} manette(s) (${withVibration} avec vibration)`,
        gamepads: connected
      }))
    } else {
      setGamepadState(prev => ({
        ...prev,
        connected: false,
        status: '❌ Aucune manette détectée',
        gamepads: []
      }))
    }
  }, [])

  // Vibration DIRECTE - exactement comme le script de test qui fonctionne
  const triggerVibration = useCallback((side, duration = 150) => {
    if (!gamepadState.enabled) return

    // Récupérer les gamepads FRAIS à chaque appel (comme dans le script de test)
    const gamepads = navigator.getGamepads()
    const intensity = gamepadState.intensity

    // Trouver le gamepad pour ce côté
    for (let i = 0; i < gamepads.length; i++) {
      const gp = gamepads[i]
      if (!gp) continue
      
      const gpSide = sideConfigRef.current[gp.index] || (gp.index % 2 === 0 ? 'left' : 'right')
      
      if (gpSide === side && gp.vibrationActuator) {
        // Log pour débogage (léger)
        if (Math.random() < 0.05) console.log('⚡️ VIB', side, duration)

        // Appel DIRECT comme dans le script de test qui fonctionne
        gp.vibrationActuator.playEffect("dual-rumble", {
          duration: duration,
          strongMagnitude: intensity,
          weakMagnitude: intensity
        })
        return
      }
    }
  }, [gamepadState.enabled, gamepadState.intensity])

  // Assigner un côté à un slot
  const assignGamepadSide = useCallback((index, side) => {
    sideConfigRef.current[index] = side
    // Mettre à jour l'affichage
    setGamepadState(prev => ({
      ...prev,
      gamepads: prev.gamepads.map(gp => 
        gp.index === index ? { ...gp, side } : gp
      )
    }))
  }, [])

  const updateEnabled = useCallback((enabled) => {
    setGamepadState(prev => ({ ...prev, enabled }))
  }, [])

  const updateIntensity = useCallback((intensity) => {
    setGamepadState(prev => ({ ...prev, intensity }))
  }, [])

  // Test de vibration DIRECT - exactement comme le script de test
  const testVibration = useCallback((side) => {
    console.log('🧪 TEST:', side)
    
    // Récupérer les gamepads FRAIS (comme dans le script de test)
    const gamepads = navigator.getGamepads()
    
    for (let i = 0; i < gamepads.length; i++) {
      const gp = gamepads[i]
      if (!gp) continue
      
      const gpSide = sideConfigRef.current[gp.index] || (gp.index % 2 === 0 ? 'left' : 'right')
      
      if (gpSide === side) {
        console.log('🎮 Manette trouvée slot', i, ':', gp.id.substring(0, 30))
        
        if (gp.vibrationActuator) {
          console.log('🎮 Lancement vibration...')
          // EXACTEMENT comme dans le script de test qui fonctionne
          gp.vibrationActuator.playEffect("dual-rumble", {
            duration: 600,
            strongMagnitude: 1.0,
            weakMagnitude: 1.0
          })
          console.log('✅ Commande envoyée!')
        } else {
          console.log('❌ Pas de vibrationActuator')
        }
        return
      }
    }
    
    console.log('❌ Aucune manette pour côté:', side)
  }, [])

  // Écouter les événements de connexion/déconnexion
  useEffect(() => {
    const handleGamepadConnected = (e) => {
      console.log('🎮 Connecté:', e.gamepad.id)
      scanGamepads()
    }

    const handleGamepadDisconnected = (e) => {
      console.log('🎮 Déconnecté:', e.gamepad.id)
      scanGamepads()
    }

    window.addEventListener('gamepadconnected', handleGamepadConnected)
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected)

    scanGamepads()
    const interval = setInterval(scanGamepads, 2000)

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected)
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected)
      clearInterval(interval)
    }
  }, [scanGamepads])

  return {
    gamepadState,
    triggerVibration,
    assignGamepadSide,
    updateEnabled,
    updateIntensity,
    scanGamepads,
    testVibration
  }
}
